import React, { RefObject, useEffect, useMemo, useState } from "react";
import pluralize from "pluralize";
import { Button, Stack } from "@chakra-ui/react";
import { FiArrowLeft, FiArrowRight, FiArrowUp } from "react-icons/fi";
import { Link as GatsbyLink } from "gatsby";
import { PromptFuncType } from "@components/CourseNav/CourseNav";
import { partition } from "lodash";

function findTasks() {
  const tasks = Array.from(
    document.querySelectorAll<HTMLInputElement>("[data-task]")
  );
  const [completeTasks, incompleteTasks] = partition(tasks, "checked");
  return {
    tasks,
    incompleteTasks,
    completeTasks,
  };
}

type LessonPaginationProps = {
  lessonIndex: number;
  headerRef: RefObject<HTMLElement>;
  lessons: readonly { slug: string }[];
  onComplete: () => void;
  prompt: PromptFuncType;
};

export function LessonPagination({
  lessonIndex,
  headerRef,
  lessons,
  onComplete,
  prompt,
}: LessonPaginationProps) {
  const [tasksRemaining, setTasksRemaining] = useState(0);

  useEffect(() => {
    // Set the initial number of remaining tasks...
    const { tasks, incompleteTasks } = findTasks();
    setTasksRemaining(incompleteTasks.length);

    function handleTaskChange() {
      const { incompleteTasks } = findTasks();
      setTasksRemaining(incompleteTasks.length);
    }

    // ...and then set up custom event handlers (see TaskEvent component) to update
    // `tasksRemaining` when a task is completed/cleared.

    tasks.forEach((task) =>
      task.addEventListener("taskchange", handleTaskChange)
    );

    return () => {
      tasks.forEach((task) =>
        task.removeEventListener("taskchange", handleTaskChange)
      );
    };
  }, []);

  const tasksRemainingButton = useMemo(() => {
    // If there are remaining tasks in the current lesson, show a button that
    // sends the user back to the incomplete tasks.
    if (tasksRemaining) {
      return (
        <Button
          size="lg"
          mr={2}
          variant="outline"
          rightIcon={<FiArrowUp />}
          onClick={(event) => {
            event.preventDefault();

            const taskEl = document.querySelector<HTMLInputElement>(
              "[data-task]:not(:checked)"
            );

            // If an incomplete task is found in the document, scroll it into view
            // and make it flash ✨. It should be found, since tasksRemaining
            // is truthy.
            if (taskEl) {
              const { task: taskId } = taskEl.dataset;
              if (taskId) {
                const element =
                  document.getElementById(taskId) ||
                  document.querySelector(`[data-tasks~="${taskId}"]`);

                if (element) {
                  /**
                   * Remove the class and then wait until the browser has
                   * repainted the flash-less div, then apply it again.
                   *
                   * See https://developer.mozilla.org/en-US/docs/Web/CSS/CSS_Animations/Tips#run_an_animation_again
                   */
                  element.classList.remove("flash");
                  requestAnimationFrame(() => {
                    requestAnimationFrame(() => {
                      element.classList.add("flash");
                    });
                  });

                  if (headerRef.current) {
                    const { marginBottom } = getComputedStyle(
                      headerRef.current
                    );
                    window.scrollTo({
                      top:
                        element.offsetTop -
                        parseInt(marginBottom, 10) -
                        headerRef.current.offsetHeight,
                      behavior: "smooth",
                    });

                    // give the incomplete task focus, but prevent the default
                    // behaviour of scrolling it into view, since that's what
                    // we're doing with window.scrollTo above
                    element.focus({
                      preventScroll: true,
                    });

                    window.gtag?.("event", "incomplete_task");
                  }
                }
              }
            }
          }}
        >
          {pluralize("task", tasksRemaining, true)} remaining
        </Button>
      );
    }
  }, [headerRef, tasksRemaining]);

  const nextButton = useMemo(() => {
    if (lessonIndex < lessons.length - 1) {
      return (
        <Button
          size="lg"
          as={GatsbyLink}
          to={`/${lessons[lessonIndex + 1].slug}`}
          rightIcon={<FiArrowRight />}
          onClick={prompt(() => window.gtag?.("event", "next_button"))}
        >
          Next
        </Button>
      );
    }

    return (
      <Button size="lg" onClick={onComplete}>
        Complete course
      </Button>
    );
  }, [lessons, lessonIndex, onComplete, prompt]);

  return (
    <Stack direction={["column", "row"]} justifyContent="space-between">
      <div>
        {lessonIndex > 0 && (
          <Button
            leftIcon={<FiArrowLeft />}
            as={GatsbyLink}
            to={`/${lessons[lessonIndex - 1].slug}`}
            onClick={prompt(() => window.gtag?.("event", "prev_button"))}
            size="lg"
          >
            Previous
          </Button>
        )}
      </div>
      <div>
        {tasksRemainingButton}
        {lessonIndex < lessons.length - 1 && nextButton}
      </div>
    </Stack>
  );
}
